import React, { useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Text, Stars } from '@react-three/drei'
import * as THREE from 'three'

/**
 * Component đồ ăn 3D
 */
const Food: React.FC<{ position: [number, number, number]; type: 'pizza' | 'burger' | 'cake' }> = ({ 
  position, 
  type 
}) => {
  const meshRef = useRef<THREE.Group>(null)

  // Animation xoay và nhấp nhô
  useFrame(({ clock }) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = clock.getElapsedTime() * 0.5
      meshRef.current.position.y = position[1] + Math.sin(clock.getElapsedTime() * 2) * 0.1
    }
  })

  const renderFood = () => {
    switch (type) {
      case 'pizza':
        return (
          <>
            <mesh>
              <cylinderGeometry args={[0.5, 0.5, 0.1, 8]} />
              <meshStandardMaterial color="#ff6b35" />
            </mesh>
            <mesh position={[0.2, 0.1, 0]}>
              <sphereGeometry args={[0.08, 8, 8]} />
              <meshStandardMaterial color="#c1121f" />
            </mesh>
            <mesh position={[-0.2, 0.1, 0.1]}>
              <sphereGeometry args={[0.08, 8, 8]} />
              <meshStandardMaterial color="#c1121f" />
            </mesh>
          </>
        )
      case 'burger':
        return (
          <>
            <mesh position={[0, 0.3, 0]}>
              <sphereGeometry args={[0.4, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
              <meshStandardMaterial color="#d4a574" />
            </mesh>
            <mesh position={[0, 0.15, 0]}>
              <cylinderGeometry args={[0.35, 0.35, 0.1]} />
              <meshStandardMaterial color="#6b8e23" />
            </mesh>
            <mesh position={[0, 0.05, 0]}>
              <cylinderGeometry args={[0.38, 0.38, 0.15]} />
              <meshStandardMaterial color="#8b4513" />
            </mesh>
            <mesh position={[0, -0.05, 0]}>
              <sphereGeometry args={[0.4, 16, 16, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
              <meshStandardMaterial color="#d4a574" />
            </mesh>
          </>
        )
      case 'cake':
        return (
          <>
            <mesh>
              <cylinderGeometry args={[0.4, 0.4, 0.3, 16]} />
              <meshStandardMaterial color="#ff69b4" />
            </mesh>
            <mesh position={[0, 0.3, 0]}>
              <cylinderGeometry args={[0.35, 0.35, 0.15, 16]} />
              <meshStandardMaterial color="#ffb3d9" />
            </mesh>
            <mesh position={[0, 0.45, 0]}>
              <coneGeometry args={[0.05, 0.3, 8]} />
              <meshStandardMaterial color="#ffd700" emissive="#ff6b00" emissiveIntensity={0.5} />
            </mesh>
          </>
        )
    }
  }

  return (
    <group ref={meshRef} position={position}>
      {renderFood()}
    </group>
  )
}

/**
 * Nhân vật ăn uống
 */
const Character: React.FC = () => {
  const meshRef = useRef<THREE.Group>(null)
  const mouthRef = useRef<THREE.Mesh>(null)

  useFrame(({ clock }) => {
    if (meshRef.current) {
      // Nhân vật nhún nhảy
      meshRef.current.position.y = Math.sin(clock.getElapsedTime() * 2) * 0.05
    }

    if (mouthRef.current) {
      // Miệng nhai
      const scale = 1 + Math.sin(clock.getElapsedTime() * 5) * 0.3
      mouthRef.current.scale.y = scale
    }
  })

  return (
    <group ref={meshRef} position={[0, 0, 2]}>
      {/* Đầu */}
      <mesh position={[0, 1.5, 0]}>
        <sphereGeometry args={[0.4, 32, 32]} />
        <meshStandardMaterial color="#ffdbac" />
      </mesh>

      {/* Mắt */}
      <mesh position={[-0.15, 1.6, 0.35]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      <mesh position={[0.15, 1.6, 0.35]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color="#000000" />
      </mesh>

      {/* Miệng cười */}
      <mesh ref={mouthRef} position={[0, 1.35, 0.35]}>
        <torusGeometry args={[0.15, 0.03, 16, 32, Math.PI]} />
        <meshStandardMaterial color="#000000" />
      </mesh>

      {/* Thân */}
      <mesh position={[0, 0.7, 0]}>
        <cylinderGeometry args={[0.3, 0.4, 0.8, 32]} />
        <meshStandardMaterial color="#4ade80" />
      </mesh>

      {/* Chân */}
      <mesh position={[-0.15, 0.15, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 0.6, 16]} />
        <meshStandardMaterial color="#3b82f6" />
      </mesh>
      <mesh position={[0.15, 0.15, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 0.6, 16]} />
        <meshStandardMaterial color="#3b82f6" />
      </mesh>
    </group>
  )
}

/**
 * Component cảnh 3D phòng chiến thắng
 */
const SuccessRoomScene3D: React.FC = () => {
  return (
    <>
      {/* Ánh sáng sáng sủa */}
      <ambientLight intensity={0.8} />
      <pointLight position={[0, 5, 0]} intensity={50} color="#fbbf24" />
      <pointLight position={[3, 3, 3]} intensity={30} color="#60a5fa" />
      <pointLight position={[-3, 3, 3]} intensity={30} color="#f472b6" />

      {/* Sao lấp lánh */}
      <Stars radius={50} depth={50} count={1000} factor={4} saturation={0} fade speed={1} />

      {/* Sàn nhà sáng */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]} receiveShadow>
        <planeGeometry args={[15, 15]} />
        <meshStandardMaterial color="#fef3c7" metalness={0.5} roughness={0.3} />
      </mesh>

      {/* Nhân vật */}
      <Character />

      {/* Đồ ăn xung quanh */}
      <Food position={[-2, 0.5, 0]} type="pizza" />
      <Food position={[2, 0.5, 0]} type="burger" />
      <Food position={[0, 0.5, -2]} type="cake" />

      {/* Bàn ăn */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[1.5, 1.5, 0.1, 32]} />
        <meshStandardMaterial color="#92400e" metalness={0.3} roughness={0.7} />
      </mesh>

      {/* Text chiến thắng */}
      <Text
        position={[0, 3.5, -1]}
        fontSize={0.5}
        color="#fbbf24"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.02}
        outlineColor="#000000"
      >
        🎉 CHIẾN THẮNG! 🎉
      </Text>

      <OrbitControls
        enableZoom={false}
        enablePan={false}
        autoRotate
        autoRotateSpeed={1}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI / 2}
      />
    </>
  )
}

/**
 * Component wrapper với Canvas
 */
export const SuccessRoomScene: React.FC = () => {
  return (
    <div className="w-full h-[500px] rounded-xl overflow-hidden shadow-2xl">
      <Canvas
        camera={{ position: [0, 2, 5], fov: 60 }}
        shadows
        gl={{ antialias: true }}
        style={{ background: 'linear-gradient(to bottom, #fef3c7, #fde68a)' }}
      >
        <SuccessRoomScene3D />
      </Canvas>
    </div>
  )
}

